import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Modal, ModalHeader, ModalBody, ModalFooter, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { getProfilePermissions, patchProfileAcl } from '@/services/profiles';
import { Button } from '@/components/Buttons';
import { useAsyncState } from '@/utils/hooks';
import { ProfileMemberDetails, ProfileMemberPermissions } from '@/types';
import ProfilePermissionsEditor from '@/components/profile/ProfilePermissionsEditor';

interface ProfilePermissionsEditDialogProps {
    show: boolean;
    memberDetails: ProfileMemberDetails | null;
    onAccept(newPermissions: ProfileMemberPermissions): void;
    onClose(): void;
}

const ProfilePermissionsEditDialog: React.FC<ProfilePermissionsEditDialogProps> = ({
    show,
    onAccept,
    onClose,
    memberDetails,
}) => {
    const [saving, setSaving] = useState(false);
    const [newPermissions, setNewPermissions] = useState<ProfileMemberPermissions | null>(null);
    const [permissions] = useAsyncState(() => getProfilePermissions(), [], null);

    const handleOpen = () => {
        setNewPermissions(
            memberDetails
                ? {
                      profilePermissions: memberDetails.profilePermissions,
                      isAdministrator: memberDetails.isAdministrator,
                  }
                : {
                      profilePermissions: [],
                      isAdministrator: false,
                  }
        );
        setSaving(false);
    };

    const handleClosed = () => {
        setNewPermissions(null);
    };

    const handleAcceptClick = async () => {
        if (!memberDetails || !newPermissions) {
            return;
        }

        setSaving(true);

        try {
            await patchProfileAcl(memberDetails.profileMemberId, newPermissions);
            triggerHandler(onAccept, newPermissions);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const handleChange = (permissions) => {
        setNewPermissions(permissions);
    };

    const renderBody = () => {
        if (!permissions || !newPermissions) {
            return <LoadingIndicator />;
        }

        return <ProfilePermissionsEditor value={newPermissions || []} onChange={handleChange} />;
    };

    return (
        <Modal
            size="lg"
            isOpen={show}
            toggle={handleCancelClick}
            onOpen={handleOpen}
            onClosed={handleClosed}>
            <ModalHeader>
                <Trans>Edit {memberDetails ? memberDetails.displayName : ''} permissions</Trans>
            </ModalHeader>
            <ModalBody>{renderBody()}</ModalBody>
            <ModalFooter>
                <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button color="primary" onClick={handleAcceptClick} loading={saving}>
                    <Trans>Save</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ProfilePermissionsEditDialog;